import { useCallback, useMemo, useState } from "react"
import { TopBarNavigation } from "../../../navigation/TopBarNavigation"
import {
  ListItem,
  ListItemText,
  LinearProgress,
  ListItemIcon,
  Typography,
  Box,
  useTheme,
  Button,
  Divider,
  ListItemButton,
} from "@mui/material"
import { Page } from "../../../common/Page"
import {
  DeleteRounded,
  ImageRounded,
  StorageRounded,
} from "@mui/icons-material"
import { useStorageUse } from "../../../settings/useStorageUse"
import { BookList } from "../../../books/lists"
import { bookActionDrawerSignal } from "../../../books/drawer/BookActionsDrawer"
import { useDownloadedFilesInfo } from "../../../download/useDownloadedFilesInfo"
import { useRemoveDownloadFile } from "../../../download/useRemoveDownloadFile"
import { difference, formatBytes } from "@oboku/shared"
import Alert from "@mui/material/Alert"
import { Logger } from "../../../debug/logger.shared"
import { useEffect } from "react"
import { useRemoveCoversInCache } from "../../../covers/useRemoveCoversInCache"
import { useDownloadedBooks } from "../../../download/useDownloadedBooks"
import { useBooks } from "../../../books/states"
import { useRemoveAllDownloads } from "../../../settings/useRemoveAllDownloads"
import { StoragePersistenceListItem } from "./StoragePersistenceListItem"
import { PageContainer } from "../../../common/PageContainer"
import { PageSection } from "../../../common/PageSection"

const bookListStyle = { width: "100%" }

export const ManageStorageScreen = () => {
  const books = useDownloadedBooks()
  const bookIds = useMemo(() => books?.map((book) => book._id) ?? [], [books])
  const { data: visibleBooks } = useBooks()
  const visibleBookIds = useMemo(
    () => visibleBooks?.map((item) => item._id) ?? [],
    [visibleBooks],
  )
  const {
    quotaUsed,
    quota,
    usage,
    covers,
    coversWeightBytes,
    refreshStorageEstimate,
  } = useStorageUse()
  const { mutate: removeCoversInCache } = useRemoveCoversInCache()
  const { mutateAsync: removeDownloadFile } = useRemoveDownloadFile()
  const { data: downloadedBooks = [], refetch: refetchDownloadedFilesInfo } =
    useDownloadedFilesInfo()
  const downloadedBookIds = downloadedBooks.map(({ id }) => id)
  const extraDownloadFilesIds = difference(downloadedBookIds, bookIds)
  const theme = useTheme()
  const bookIdsToDisplay = useMemo(
    () => bookIds.filter((id) => visibleBookIds?.includes(id)),
    [bookIds, visibleBookIds],
  )
  const { mutate: removeAllDownloads } = useRemoveAllDownloads({
    onSuccess: () => {
      refetchDownloadedFilesInfo()
    },
  })
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null)

  const removeExtraBooks = useCallback(() => {
    Promise.all(
      extraDownloadFilesIds.map((id) => removeDownloadFile({ bookId: id })),
    )
      .then(() => refetchDownloadedFilesInfo())
      .catch(Logger.error)
  }, [refetchDownloadedFilesInfo, extraDownloadFilesIds, removeDownloadFile])

  const onItemClick = useCallback(
    (id: string) =>
      bookActionDrawerSignal.update({
        openedWith: id,
        actions: ["removeDownload"],
      }),
    [],
  )

  useEffect(() => {
    void books

    refreshStorageEstimate()
    refetchDownloadedFilesInfo()
  }, [books, refreshStorageEstimate, refetchDownloadedFilesInfo])

  return (
    <Page ref={setScrollerEl} bottomGutter={false}>
      <TopBarNavigation title={"Manage storage"} />
      <PageContainer disableGutters>
        <PageSection>
          <ListItem>
            <ListItemIcon>
              <StorageRounded />
            </ListItemIcon>
            <ListItemText
              primary="Available storage"
              disableTypography
              secondary={
                <div>
                  <Box
                    sx={{
                      marginY: 1,
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={quotaUsed * 100}
                    />
                  </Box>
                  <Typography
                    gutterBottom
                    variant="body2"
                  >{`${formatBytes(usage)} used of ${formatBytes(quota)} (${(quotaUsed * 100).toFixed(2)}%)`}</Typography>
                </div>
              }
            />
          </ListItem>
          <StoragePersistenceListItem />
          <ListItemButton onClick={() => removeCoversInCache()}>
            <ListItemIcon>
              <ImageRounded />
            </ListItemIcon>
            <ListItemText
              primary="Delete all covers cached"
              secondary={`${covers} covers are cached for a total of ${formatBytes(coversWeightBytes)}`}
            />
          </ListItemButton>
          {bookIdsToDisplay.length > 0 && (
            <ListItemButton onClick={() => removeAllDownloads()}>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary="Delete all downloads"
                secondary="Local books will not be deleted"
              />
            </ListItemButton>
          )}
        </PageSection>
      </PageContainer>
      {extraDownloadFilesIds.length > 0 && (
        <PageContainer>
          <Alert severity="warning" style={{ marginBottom: theme.spacing(0) }}>
            <div style={{ marginBottom: theme.spacing(1) }}>
              It seems that you have some extra downloaded files not linked to
              your current library.
            </div>
            <Button
              variant="outlined"
              color="secondary"
              onClick={removeExtraBooks}
            >
              Remove to free up space
            </Button>
          </Alert>
        </PageContainer>
      )}
      <PageContainer disableGutters>
        <Divider />
        <BookList
          viewMode={"list"}
          data={bookIdsToDisplay}
          density="dense"
          withDrawerActions={false}
          style={bookListStyle}
          customScrollParent={scrollerEl ?? undefined}
          onItemClick={onItemClick}
        />
      </PageContainer>
    </Page>
  )
}
